import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import {
  clearAccessToken,
  getAccessToken,
  fetchWithAuth,
  updateUser,
  API_URL,
} from "../utils/fetchFunctions";
import { getUserRole } from "../utils/jwtUtils";
import FormContainer from "../components/FormContainer";
import Button from "../components/Button";
import InputField from "../components/InputField";
import "./profile.css";

const FIELD_CONFIG = [
  { key: "company_name", label: "Company Name", type: "text" },
  { key: "contact_name", label: "Contact Name", type: "text" },
  { key: "phone_number", label: "Phone Number", type: "tel" },
  { key: "website", label: "Website", type: "url" },
];

const Profile = () => {
  const [user, setUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    company_name: "",
    contact_name: "",
    phone_number: "",
    website: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const fetchingRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (fetchingRef.current) return;

    const token = getAccessToken();
    if (!token) {
      navigate("/login");
      return;
    }

    const role = getUserRole();
    setUserRole(role);

    const fetchUser = async () => {
      fetchingRef.current = true;
      setError(null);

      try {
        const response = await fetchWithAuth(`${API_URL}/api/user`, {
          method: "GET",
        });

        if (!response.ok) {
          if (response.status === 401) {
            clearAccessToken();
            navigate("/login");
            return;
          }
          throw new Error("User data could not be loaded.");
        }

        const data = await response.json();

        const normalized = {
          id: data.id,
          email: data.email,
          company_name: data.company_name ?? null,
          contact_name: data.contact_name ?? null,
          phone_number: data.phone_number ?? null,
          website: data.website ?? null,
        };

        setUser(normalized);
      } catch (err) {
        setError(
          err.message || "Network error. Please try again."
        );
      } finally {
        fetchingRef.current = false;
      }
    };

    fetchUser();
  }, [navigate]);

  useEffect(() => {
    if (location.state?.passwordChanged) {
      setShowBanner(true);
      navigate(location.pathname, { replace: true });

      const timer = setTimeout(() => {
        setShowBanner(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [location.state?.passwordChanged, navigate, location.pathname]);

  const handleInputChange = (field) => (e) => {
    setEditFormData({
      ...editFormData,
      [field]: e.target.value,
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditFormData({
      company_name: user.company_name || "",
      contact_name: user.contact_name || "",
      phone_number: user.phone_number || "",
      website: user.website || "",
    });
  };

  const handleCancelClick = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const response = await updateUser(editFormData);
      setUser(response.updatedUser);
      setIsEditing(false);
      setShowSaveSuccessModal(true);
    } catch (err) {
      setError(err.message || "Error saving.");
    } finally {
      setIsSaving(false);
    }
  };

  const formatValue = (value) => {
    if (!value || value.trim() === "") return "No information";
    return value;
  };

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          {error ? (
            <>
              <p className="profile-error__message">{error}</p>
              <Button onClick={() => window.location.reload()}>
                Refresh page
              </Button>
            </>
          ) : (
            <div className="profile-loading__text">Loading…</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <FormContainer className="profile-card">
        <h1 className="profile-title">
          My Profile
          {userRole === "admin" && (
            <span className="profile-title__admin-badge">admin</span>
          )}
        </h1>

        {error && <p className="profile-error__message">{error}</p>}

        {isEditing ? (
          <>
            <div className="profile-info">
              <div className="profile-field">
                <label className="profile-field__label">Email</label>
                <input
                  type="email"
                  className="profile-field__input profile-field__input--readonly"
                  value={user.email}
                  readOnly
                />
              </div>

              {FIELD_CONFIG.map(({ key, label, type }) => (
                <InputField
                  key={key}
                  id={key}
                  type={type}
                  label={label}
                  value={editFormData[key]}
                  onChange={handleInputChange(key)}
                />
              ))}
            </div>

            <div className="profile-actions profile-actions--edit">
              <Button
                className="profile-button"
                loading={isSaving}
                onClick={handleSave}
              >
                Save
              </Button>
              <Button
                className="profile-button"
                onClick={handleCancelClick}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="profile-info">
              <div className="profile-field">
                <label className="profile-field__label">Email</label>
                <div className="profile-field__value">{user.email}</div>
              </div>

              {FIELD_CONFIG.map(({ key, label }) => (
                <div key={key} className="profile-field">
                  <label className="profile-field__label">{label}</label>
                  <div className="profile-field__value">
                    {formatValue(user[key])}
                  </div>
                </div>
              ))}
            </div>

            <div className="profile-actions profile-actions--edit">
              <Button
                className="profile-button profile-button--edit"
                onClick={handleEditClick}
              >
                Edit Profile
              </Button>

              <Button
                className="profile-button profile-button--edit"
                onClick={() => navigate("/change-password")}
              >
                Change Password
              </Button>
            </div>
          </>
        )}
      </FormContainer>

      {showBanner &&
        createPortal(
          <div className="modal-overlay" onClick={() => setShowBanner(false)}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
              <p className="modal-message">
                Password changed successfully.
              </p>
              <button
                className="modal-button"
                onClick={() => setShowBanner(false)}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}

      {showSaveSuccessModal &&
        createPortal(
          <div
            className="modal-overlay"
            onClick={() => setShowSaveSuccessModal(false)}
          >
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">Successfully saved</h2>
              <p className="modal-message">
                Your profile data has been successfully saved.
              </p>
              <button
                className="modal-button"
                onClick={() => setShowSaveSuccessModal(false)}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Profile;
